(function () {
  const DRAG_THRESHOLD = 4;
  const SNAP_RATIO = 0.18;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getVisibleItems(container, selector) {
    return Array.from(container.querySelectorAll(selector)).filter(
      (element) => element.clientWidth > 0 && !element.hidden
    );
  }

  function getStep(items, fallback) {
    if (items.length < 2) return fallback;
    const step = Math.abs(items[1].offsetLeft - items[0].offsetLeft);
    return step > 0 ? step : fallback;
  }

  function isLightboxControl(target) {
    return Boolean(
      target.closest(
        'button[is="media-lightbox-button"], button[is="media-hover-button"], button.button--close, .shopify-model-viewer-ui__controls-area, a, input, textarea, select, label'
      )
    );
  }

  function setSlideWidths(slider) {
    const width = slider.clientWidth;
    if (!width) return;
    slider.style.setProperty('--wd-gallery-slide-width', `${width}px`);
  }

  function getSlides(slider) {
    const track = slider.querySelector('.wd-gallery__slides');
    if (!track) return [];
    return Array.from(track.querySelectorAll('.product__media')).filter((element) => !element.hidden);
  }

  function getMaxScroll(slider) {
    return Math.max(0, slider.scrollWidth - slider.clientWidth);
  }

  function getSlidePositions(slider) {
    const maxScroll = getMaxScroll(slider);
    const slides = getSlides(slider);
    if (!slides.length) return [0];

    return slides.map((slide, index) => {
      if (index === slides.length - 1) return maxScroll;
      return Math.min(maxScroll, slide.offsetLeft);
    });
  }

  function getSlideIndex(slider) {
    const positions = getSlidePositions(slider);
    if (!positions.length) return 0;

    return positions.reduce((closestIndex, position, index) => (
      Math.abs(position - slider.scrollLeft) < Math.abs(positions[closestIndex] - slider.scrollLeft)
        ? index
        : closestIndex
    ), 0);
  }

  function initMainDrag(slider) {
    if (!slider || slider.dataset.wdDragReady === 'true') return;

    const track = slider.querySelector('.wd-gallery__slides');
    if (!track) return;

    const SNAP_MS = 280;
    let tracking = false;
    let dragging = false;
    let animating = false;
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let currentOffset = 0;
    let startIndex = 0;
    let settleTimer = 0;
    let rafId = 0;
    let pendingOffset = 0;

    const clearTransform = () => {
      track.style.transition = '';
      track.style.transform = '';
      track.style.willChange = '';
    };

    const paintOffset = () => {
      rafId = 0;
      track.style.transform = `translate3d(${pendingOffset}px, 0, 0)`;
    };

    const applyOffset = (offset, animate) => {
      currentOffset = offset;
      pendingOffset = offset;
      track.style.willChange = 'transform';

      if (animate && !prefersReducedMotion()) {
        track.style.transition = `transform ${SNAP_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        track.style.transform = `translate3d(${offset}px, 0, 0)`;
        return;
      }

      track.style.transition = 'none';
      if (!rafId) rafId = window.requestAnimationFrame(paintOffset);
    };

    const settleToIndex = (index) => {
      const positions = getSlidePositions(slider);
      const maxIndex = Math.max(0, positions.length - 1);
      const targetIndex = Math.max(0, Math.min(maxIndex, index));
      const targetLeft = positions[targetIndex] || 0;
      const targetOffset = startScroll - targetLeft;

      window.clearTimeout(settleTimer);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }

      animating = true;
      applyOffset(targetOffset, true);

      const finish = () => {
        animating = false;
        slider.scrollLeft = targetLeft;
        clearTransform();
        currentOffset = 0;
        startScroll = targetLeft;
        slider.style.scrollSnapType = '';
        slider.style.scrollBehavior = '';
        slider.style.touchAction = '';
        slider.dispatchEvent(new Event('scroll', { bubbles: true }));
      };

      if (prefersReducedMotion() || Math.abs(targetOffset) < 1) {
        finish();
        return;
      }

      settleTimer = window.setTimeout(finish, SNAP_MS + 20);
    };

    const resetPointer = () => {
      tracking = false;
      dragging = false;
      pointerId = null;
      slider.classList.remove('is-dragging');
    };

    const endDrag = (event) => {
      if (!tracking || (event && event.pointerId !== pointerId)) return;

      const wasDragging = dragging;

      if (pointerId !== null && wasDragging) {
        try {
          slider.releasePointerCapture(pointerId);
        } catch (_) {
          /* already released */
        }
      }

      resetPointer();

      if (!wasDragging) {
        clearTransform();
        slider.style.scrollSnapType = '';
        slider.style.scrollBehavior = '';
        slider.style.touchAction = '';
        return;
      }

      slider.dataset.wdJustDragged = '1';
      window.requestAnimationFrame(() => delete slider.dataset.wdJustDragged);

      const width = slider.clientWidth || 1;
      const desiredScroll = startScroll - currentOffset;
      const deltaRatio = (desiredScroll - startScroll) / width;
      let targetIndex = startIndex;

      if (deltaRatio > SNAP_RATIO) {
        targetIndex = startIndex + 1;
      } else if (deltaRatio < -SNAP_RATIO) {
        targetIndex = startIndex - 1;
      }

      settleToIndex(targetIndex);
    };

    slider.addEventListener(
      'click',
      (event) => {
        if (slider.dataset.wdJustDragged === '1') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    slider.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        if (event.pointerType === 'touch') return;
        if (animating) return;
        if (isLightboxControl(event.target)) return;

        setSlideWidths(slider);
        if (getMaxScroll(slider) <= 1) return;

        const positions = getSlidePositions(slider);

        window.clearTimeout(settleTimer);
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
        clearTransform();

        tracking = true;
        dragging = false;
        pointerId = event.pointerId;
        startX = event.clientX;
        startIndex = getSlideIndex(slider);
        startScroll = positions[startIndex] || 0;
        slider.scrollLeft = startScroll;
        currentOffset = 0;
        pendingOffset = 0;
      },
      { passive: true }
    );

    slider.addEventListener(
      'pointermove',
      (event) => {
        if (!tracking || event.pointerId !== pointerId) return;

        const delta = event.clientX - startX;
        if (!dragging) {
          if (Math.abs(delta) < DRAG_THRESHOLD) return;

          dragging = true;
          slider.classList.add('is-dragging');
          slider.style.scrollSnapType = 'none';
          slider.style.scrollBehavior = 'auto';
          slider.style.touchAction = 'none';
          slider.setPointerCapture(pointerId);
        }

        event.preventDefault();
        const maxScroll = getMaxScroll(slider);
        const desiredScroll = Math.max(0, Math.min(maxScroll, startScroll - delta));
        applyOffset(startScroll - desiredScroll, false);
      },
      { passive: false }
    );

    slider.addEventListener('pointerup', endDrag);
    slider.addEventListener('pointercancel', endDrag);
    slider.addEventListener('lostpointercapture', () => {
      if (dragging) endDrag({ pointerId });
      else resetPointer();
    });

    const resizeObserver = new ResizeObserver(() => {
      if (dragging || animating) return;
      const index = getSlideIndex(slider);
      setSlideWidths(slider);
      const positions = getSlidePositions(slider);
      slider.scrollLeft = positions[index] || 0;
    });
    resizeObserver.observe(slider);

    setSlideWidths(slider);
    slider.dataset.wdDragReady = 'true';
  }

  function initThumbDrag(thumbList) {
    if (!thumbList || thumbList.dataset.wdThumbDragReady === 'true') return;

    let tracking = false;
    let dragging = false;
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;

    const reset = () => {
      tracking = false;
      dragging = false;
      pointerId = null;
      thumbList.classList.remove('is-dragging');
      thumbList.style.touchAction = '';
      thumbList.style.scrollBehavior = '';
      thumbList.style.cursor = '';
    };

    const endDrag = (event) => {
      if (!tracking || (event && event.pointerId !== pointerId)) return;

      const wasDragging = dragging;

      if (pointerId !== null && wasDragging) {
        try {
          thumbList.releasePointerCapture(pointerId);
        } catch (_) {
          /* already released */
        }
      }

      reset();

      if (wasDragging) {
        thumbList.dataset.wdJustDragged = '1';
        window.requestAnimationFrame(() => delete thumbList.dataset.wdJustDragged);
      }
    };

    thumbList.addEventListener(
      'click',
      (event) => {
        if (thumbList.dataset.wdJustDragged === '1') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    thumbList.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        if (event.pointerType === 'touch') return;
        if (thumbList.scrollWidth <= thumbList.clientWidth + 1) return;

        tracking = true;
        dragging = false;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = thumbList.scrollLeft;
      },
      { passive: true }
    );

    thumbList.addEventListener(
      'pointermove',
      (event) => {
        if (!tracking || event.pointerId !== pointerId) return;

        const delta = event.clientX - startX;
        if (!dragging) {
          if (Math.abs(delta) < DRAG_THRESHOLD) return;
          dragging = true;
          thumbList.classList.add('is-dragging');
          thumbList.style.scrollBehavior = 'auto';
          thumbList.style.touchAction = 'none';
          thumbList.style.cursor = 'grabbing';
          thumbList.setPointerCapture(pointerId);
        }

        event.preventDefault();
        const maxScroll = Math.max(0, thumbList.scrollWidth - thumbList.clientWidth);
        thumbList.scrollLeft = Math.max(0, Math.min(maxScroll, startScroll - delta));
      },
      { passive: false }
    );

    thumbList.addEventListener('pointerup', endDrag);
    thumbList.addEventListener('pointercancel', endDrag);
    thumbList.addEventListener('lostpointercapture', () => {
      if (dragging) endDrag({ pointerId });
      else reset();
    });

    thumbList.dataset.wdThumbDragReady = 'true';
  }

  function initThumbNav(gallery) {
    const thumbList = gallery.querySelector('[data-wd-thumb-list]');
    const prevBtn = gallery.querySelector('[data-wd-thumb-prev]');
    const nextBtn = gallery.querySelector('[data-wd-thumb-next]');
    if (!thumbList || thumbList.dataset.wdThumbReady === 'true') return;

    initThumbDrag(thumbList);

    const thumbStep = () => {
      const thumbs = getVisibleItems(thumbList, '.product__thumbnail');
      const styles = getComputedStyle(thumbList);
      const gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
      return getStep(thumbs, (thumbs[0]?.offsetWidth || 110) + gap);
    };

    const scrollByOne = (direction) => {
      thumbList.scrollBy({
        left: direction * thumbStep(),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    const updateButtons = () => {
      if (!prevBtn || !nextBtn) return;
      const max = Math.max(0, thumbList.scrollWidth - thumbList.clientWidth);
      prevBtn.disabled = thumbList.scrollLeft <= 2;
      nextBtn.disabled = thumbList.scrollLeft >= max - 2;
    };

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener('click', () => scrollByOne(-1));
      nextBtn.addEventListener('click', () => scrollByOne(1));
    }

    thumbList.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
    thumbList.dataset.wdThumbReady = 'true';
  }

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.wdGalleryReady === 'true') return;

    const slider = gallery.querySelector('.wd-gallery__slider');
    initMainDrag(slider);
    initThumbNav(gallery);
    gallery.dataset.wdGalleryReady = 'true';
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-wd-pdp-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
